import PropTypes from 'prop-types';
import { useRef, useState, useMemo } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import { ButtonBase, CardContent, ClickAwayListener, Grid, Paper, Popper, Stack, Tooltip, Typography, Box } from '@mui/material';
import DownOutlined from '@ant-design/icons/DownOutlined';

import ProfileTab from './ProfileTab';
import MainCard from 'components/MainCard';
import Transitions from 'components/@extended/Transitions';
import { useAuth } from 'sections/authentication/context/UseAuth';
import { parseJwt } from 'utils/jwt';

// Tailwind color palette options
const PROFILE_COLORS = [
  'bg-blue-500',
  'bg-green-500',
  'bg-amber-500',
  'bg-purple-500',
  'bg-rose-500',
  'bg-indigo-500',
  'bg-teal-500',
  'bg-pink-500',
  'bg-orange-500'
];

export default function Profile() {
  const theme = useTheme();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const handleToggle = () => setOpen((prevOpen) => !prevOpen);
  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  const { user } = useAuth();
  const token = user?.token;

  const payload = useMemo(() => (token ? parseJwt(token) : null), [token]);

  const email = payload?.sub || 'unknown@example.com';

  const fullRole = payload?.role || 'Unknown';
  const displayRole = fullRole
    .replace(/^ROLE_/, '') // Removes "ROLE_" prefix
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
  // Extract username
  const username = useMemo(() => email.split('@')[0], [email]);

  // Initials (first 2 letters)
  const initials = useMemo(() => username.slice(0, 2).toUpperCase(), [username]);

  // Choose deterministic Tailwind color
  const colorClass = useMemo(() => {
    const hash = Array.from(username).reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return PROFILE_COLORS[hash % PROFILE_COLORS.length];
  }, [username]);

  return (
    <Box sx={{ flexShrink: 0, ml: 'auto' }}>
      <Tooltip title="Profile" disableInteractive>
        <ButtonBase
          sx={{
            pr: 1,
            borderRadius: '999px',
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
            '&:focus-visible': {
              outline: `2px solid ${theme.vars.palette.secondary.dark}`,
              outlineOffset: 2
            }
          }}
          aria-label="open profile"
          ref={anchorRef}
          aria-controls={open ? 'profile-grow' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
        >
          {/* Avatar */}
          <div
            className={`${colorClass} text-white font-semibold w-6 h-6 rounded-full flex items-center justify-center text-xs hover:outline-1 hover:outline-primary`}
          >
            {initials}
          </div>

          {/* Name only */}
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: 'text.primary' }} noWrap>
            {username}
          </Typography>

          {/* Dropdown arrow */}
          <DownOutlined
            style={{
              fontSize: '0.65rem',
              color: theme.palette.text.secondary,
              transition: 'transform 0.2s ease',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)'
            }}
          />
        </ButtonBase>
      </Tooltip>

      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [{ name: 'offset', options: { offset: [0, 9] } }]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper
              sx={{
                boxShadow: theme.vars.customShadows.z1,
                width: 290,
                minWidth: 240,
                maxWidth: { xs: 250, md: 290 }
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard elevation={0} border={false} content={false}>
                  <CardContent sx={{ px: 2.5, pt: 3 }}>
                    <Grid container sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                      <Grid>
                        <Stack direction="row" sx={{ gap: 1.25, alignItems: 'center' }}>
                          <div className={`${colorClass} text-white font-semibold w-6 h-6 rounded-full flex items-center justify-center`}>
                            {initials}
                          </div>
                          <Stack>
                            <Typography variant="h6">{email}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {displayRole}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Grid>
                    </Grid>
                  </CardContent>

                  <ProfileTab />
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}

Profile.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number,
  index: PropTypes.number,
  other: PropTypes.any
};
